import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import axios from 'axios';
import CalendarPicker from '../util/CalendarPicker';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import CustomFieldValues from '../util/CustomFieldValues';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { transformToHierarchy } from '../util/tree';
import TreeViewComponent from '../util/TreeViewComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import UploadIcon from '@mui/icons-material/Upload';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { styled } from '@mui/material/styles';
import Pdf from '../../img/pdf.png';
import ImageUpload from '../util/ImageUpload';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface TemporaryDrawerProps {
  setShow: any,
  refreshExpenses: any,
  selectedUser: any,
  baseUrl: any,
  fields: any
}

export default function EditExpenses({ setShow, refreshExpenses, selectedUser, baseUrl, fields }: TemporaryDrawerProps) {
  const [startDate, setStartDate] = useState(new Date());
  const [isCategoryListVisible, setCategoryListVisible] = useState(false);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [categoryName, setCategoryName] = useState<any[]>([]);
  const [paymentDocumentUrl, setPaymentDocumentUrl] = useState('');
  const transformToHierarchyCallback = useCallback(transformToHierarchy, []);
  const [prevSelectedCategoryId, setPrevSelectedCategoryId] = useState<number[]>([]);
  const [category, setCategory] = useState<any[]>([]);
  const [openCheckModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [department, setDepartment] = useState('');
  const [project, setProject] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [account, setAccount] = useState('');
  const [iban, setIban] = useState('');
  const [financeInfo, setFinanceInfo] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const handleClose = () => setShow(false);
  const [active, setActive] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [imgUploadOpen, setImgUploadOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      if (selectedUser) {
        try {
          const { data } = await axios.get(`expenses/${selectedUser.id}`);
          const { data: categoryData } = await axios.get('category');
          if (data) {
            setStartDate(data.expenses.date ? new Date(data.expenses.date) : new Date());
            setDepartment(data.expenses.nominal_department || '');
            setProject(data.expenses.project || '');
            setDescription(data.expenses.description || '');
            setPaymentDocumentUrl(data.expenses.payment_document_url || '');
            setAmount(data.expenses.amount ? String(data.expenses.amount) : '');
            setActive(data.expenses.staff ?? true);
            if (data.expenses.categoryId) {
              const selectedCategory = categoryData.find((node: { id: number }) => node.id === data.expenses.categoryId);
              if (selectedCategory) {
                setCategoryName(selectedCategory.name);
              }
              setSelectedCategoryId([data.expenses.categoryId]);
              setPrevSelectedCategoryId([data.expenses.categoryId]);
            }
            if (!data.expenses.staff) {
              setName(data.expenses.name || '');
              setPhone(data.expenses.contact_number || '');
              setAccount(data.expenses.account_number || '');
              setIban(data.expenses.iban || '');
              setFinanceInfo(data.expenses.finance_additional_info || '');
              setSortCode(data.expenses.sort_code || '');
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [selectedUser, transformToHierarchyCallback]);

  const handleDateChange = (date: Date) => {
    setStartDate(date);
  };

  const handleChangeAcrive = () => {
    setActive(!active);
  }

  const handleCategoryListClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleNodeSelect = (nodeId: number) => {
    setSelectedCategoryId([nodeId])
  };

  const handleClickCategoryBox = (event: React.MouseEvent) => {
    setCategoryListVisible(!isCategoryListVisible);
    if (isCategoryListVisible) {
      setSelectedCategoryId(prevSelectedCategoryId)
    }
    setPrevSelectedCategoryId(selectedCategoryId);
    event.stopPropagation();
  };

  const handleClickAddCategoryId = (selectedId: number) => {
    const selectedCategory = allCategory?.find(node => node.id === selectedId);
    if (selectedCategory) {
      setCategoryName(selectedCategory.name)
      setCategoryListVisible(false);
    } else {
      setCategoryName(['All category'])
      console.log('Category not found');
      setCategoryListVisible(false);
    }
  };

  const isFieldShown = (column: string) => {
    const field = fields.find(
      (f: any) => f.table_name === 'expenses' && f.column_name === column
    );
    return field?.isShown;
  };

  // UX-3: check if a field is required per backend config
  const isRequired = (column: string) =>
    fields.some((f: any) => f.table_name === 'expenses' && f.column_name === column && f.isRequired);

  const STAFF_FIELDS = [
    'name',
    'contact_number',
    'account_number',
    'iban',
    'sort_code',
    'finance_additional_info',
  ];

  const isFieldAvailable = (field: any) => {
    if (field.table_name !== 'expenses') return false;
    if (!field.isRequired) return false;
    if (!field.isShown) return false;
    if (active && STAFF_FIELDS.includes(field.column_name)) return false;
    return true;
  };

  const getValue = (column: string) => {
    const map: Record<string, any> = {
      name,
      contact_number: phone,
      account_number: account,
      iban,
      sort_code: sortCode,
      finance_additional_info: financeInfo,
      project,
      description,
      nominal_department: department,
    };
    return map[column];
  };

  // BUG-5: human-readable field labels for error messages
  const FIELD_LABELS: Record<string, string> = {
    nominal_department: t('Department'),
    project: t('Project'),
    description: t('Description'),
    name: t('Name'),
    contact_number: t('Phone'),
    account_number: t('AccountNumber'),
    iban: 'IBAN',
    sort_code: t('Sort Code'),
    finance_additional_info: t('FinanceAdditionalInfo'),
  };

  const handleExpensesUpdate = async () => {
    try {
      if (!description?.trim()) {
        enqueueSnackbar(`${t('FillRequiredFields')}: ${t('Description')}`, { variant: 'error' });
        return;
      }

      if (!amount?.toString().trim()) {
        enqueueSnackbar(t('ChooseAmount'), { variant: 'error' });
        return;
      }
      if (/[.,]$/.test(amount)) {
        enqueueSnackbar(t('AmountInvalidEnd'), { variant: 'error' });
        return;
      }

      const requiredFields = fields.filter(isFieldAvailable);
      const missingFields: string[] = [];
      requiredFields.forEach((f: any) => {
        const value = getValue(f.column_name);
        if (!value || value.toString().trim() === '') {
          missingFields.push(f.column_name);
        }
      });

      if (missingFields.length > 0) {
        // BUG-5: show readable labels, not DB column names
        const labels = missingFields.map(f => FIELD_LABELS[f] ?? f).join(', ');
        enqueueSnackbar(`${t('FillRequiredFields')}: ${labels}`, { variant: 'error' });
        return;
      }

      let payload: any = {
        date: startDate,
        nominal_department: department,
        staff: active,
        description,
        project,
        amount: Number(amount),
      };
      if (!active) {
        payload = {
          ...payload,
          name,
          contact_number: phone,
          account_number: account,
          iban,
          sort_code: sortCode,
          finance_additional_info: financeInfo,
        };
      }
      if (Array.isArray(selectedCategoryId) && selectedCategoryId.length > 0) {
        payload.categoryId = Number(selectedCategoryId[0]);
      }

      await axios.put(`expenses/${selectedUser.id}`, payload);

      if (file) {
        const maxFileSize = 2 * 1024 * 1024;
        if (file.size > maxFileSize) {
          enqueueSnackbar(t('FileSizeLimit'), { variant: 'error' });
          return;
        }
        const formData = new FormData();
        formData.append('payment_document', file);
        await axios.post(`expenses/upload/${selectedUser.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      handleClose();
      setFile(null);
      refreshExpenses();
      enqueueSnackbar('Successfully updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error updating', { variant: 'error' });
      console.error('Error updating:', error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: categoryData } = await axios.get('category');
        setAllCategory(categoryData)
        const hierarchyData = transformToHierarchyCallback(categoryData);
        setCategory(hierarchyData);
      } catch (e) {
      }
    })();
  }, [transformToHierarchyCallback]);


  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddExpenses')}</div>
          <div className='fx-head-sub'>{t('CreateExpenseClaim')}</div>
        </div>
        <div className='fx-close' onClick={() => setShow(false)}>✕</div>
      </div>

      {/* Body */}
      <div className='fx-body'>
        {/* Date */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Date')} <span className='fx-req'>*</span></p>
          <CalendarPicker
            mode="single"
            variant="bare"
            className="fx-input"
            value={startDate}
            onChange={(date: any) => handleDateChange(date)}
            displayFormat="DD/MM/YYYY"
          />
        </div>

        {/* Category */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Category')} <span className='fx-req'>*</span></p>
          <CategoryTaskPicker
            nodes={category}
            selectedCategoryId={selectedCategoryId}
            categoryName={categoryName}
            onCategorySelect={(id, name) => {
              if (id === -1) { setSelectedCategoryId([]); setCategoryName([]); }
              else { setSelectedCategoryId([id]); setCategoryName(name as any); }
            }}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Receipt photo upload */}
        <div className='fx-field'>
          <p className='fx-label'>{t('BrowsePayment')} <span className='fx-req'>*</span></p>
          <div className='fx-dropzone' onClick={() => setImgUploadOpen(true)}>
            <div className='fx-dz-icon'>{file ? <UploadIcon fontSize="small" /> : <ExitToAppIcon fontSize="small" />}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className='fx-dz-main truncate'>{file ? file.name : t('BrowsePayment')}</div>
              <div className='fx-dz-sub'>{file ? t('BrowsePayment') : 'PNG, JPG, PDF, EML · ≤ 2 MB'}</div>
            </div>
            {file && (
              <FontAwesomeIcon
                icon={faXmark}
                className='cursor-pointer hover:text-red-500'
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
              />
            )}
            {paymentDocumentUrl && !file && (
              <a
                href={`${baseUrl}${paymentDocumentUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className='flex items-center'>
                <img src={Pdf} alt="PDF" className='w-[20px] h-[24px]' />
              </a>
            )}
          </div>
          <ImageUpload
            open={imgUploadOpen}
            onClose={() => setImgUploadOpen(false)}
            onComplete={({ file: f, fileName }) => {
              setFile(new File([f], fileName, { type: f.type }));
            }}
            extraAcceptTypes=".eml,.msg"
            extraExtensions={['.eml', '.msg']}
          />
        </div>

        {/* Description */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Description')} <span className='fx-req'>*</span></p>
          <textarea
            value={description || ''}
            title={t('Description')}
            className="fx-textarea"
            rows={2}
            onChange={(e: any) => setDescription(e.target.value)}
          />
        </div>

        {/* Amount */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Amount')} <span className='fx-req'>*</span></p>
          <input type="text"
            value={amount}
            title={t('Amount')}
            className="fx-input"
            onChange={(e: any) => {
              let value = e.target.value;
              value = value.replace(/[^0-9.,]/g, '');
              if ((value.match(/[.,]/g) || []).length > 1) {
                value = value.slice(0, -1);
              }
              value = value.replace(/([.,]\d{2})\d+/, '$1');
              setAmount(value);
            }}
          />
        </div>

        {/* Staff toggle */}
        <div className='fx-switch-card'>
          <Switch
            checked={active}
            onChange={handleChangeAcrive}
            inputProps={{ 'aria-label': 'controlled' }}
          />
          <div>
            <div className='fx-switch-title cursor-pointer' onClick={handleChangeAcrive}>{t('Staff')}</div>
            <div className='fx-switch-hint'>{t('StaffToggleHint')}</div>
          </div>
        </div>

        {!active && (
          <>
            <div className='fx-section'>{t('AddUserInfo')}</div>
            <div className='fx-row2'>
              {isFieldShown('name') && (
                <div className='fx-field'>
                  <p className='fx-label'>{t('Name')} {isRequired('name') && <span className='fx-req'>*</span>}</p>
                  <input type="text"
                    value={name || ''}
                    title={t('Name')}
                    className="fx-input"
                    onChange={(e: any) => setName(e.target.value)}
                  />
                </div>
              )}
              {isFieldShown('contact_number') && (
                <div className='fx-field'>
                  <p className='fx-label'>{t('Phone')} {isRequired('contact_number') && <span className='fx-req'>*</span>}</p>
                  <input type="text"
                    value={phone || ''}
                    title={t('Phone')}
                    className="fx-input"
                    onChange={(e: any) => setPhone(e.target.value)}
                  />
                </div>
              )}
            </div>
            {isFieldShown('account_number') && (
              <div className='fx-field'>
                <p className='fx-label'>{t('AccountNumber')} {isRequired('account_number') && <span className='fx-req'>*</span>}</p>
                <input type="text"
                  value={account || ''}
                  title={t('AccountNumber')}
                  className="fx-input"
                  onChange={(e: any) => setAccount(e.target.value)}
                />
              </div>
            )}
            {isFieldShown('sort_code') && (
              <div className='fx-field'>
                <p className='fx-label'>{t('Sort Code')} {isRequired('sort_code') && <span className='fx-req'>*</span>}</p>
                <input type="text"
                  value={sortCode || ''}
                  title={t('Sort Code')}
                  className="fx-input"
                  onChange={(e: any) => setSortCode(e.target.value)}
                />
              </div>
            )}
            {isFieldShown('iban') && (
              <div className='fx-field'>
                <p className='fx-label'>{t('IBAN')} {isRequired('iban') && <span className='fx-req'>*</span>}</p>
                <input type="text"
                  value={iban || ''}
                  title="IBAN"
                  className="fx-input"
                  onChange={(e: any) => setIban(e.target.value)}
                />
              </div>
            )}
            {isFieldShown('finance_additional_info') && (
              <div className='fx-field'>
                <p className='fx-label'>{t('FinanceAdditionalInfo')} {isRequired('finance_additional_info') && <span className='fx-req'>*</span>}</p>
                <textarea
                  value={financeInfo || ''}
                  title={t('FinanceAdditionalInfo')}
                  className="fx-textarea"
                  rows={2}
                  onChange={(e: any) => setFinanceInfo(e.target.value)}
                />
              </div>
            )}
          </>
        )}

        <CustomFieldValues tableName="expenses" entityId={selectedUser.id} />
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button type="button" className='fx-save' onClick={() => handleExpensesUpdate()}>
          {t('Change')}
        </button>
        <button type="button" className='fx-cancel' onClick={() => setShow(false)}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  )
}
